import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { base_colorSurfaceMutedHover } from 'ext-ui/style/colors'
import React from 'react'
import { useDispatch } from 'react-redux'
import AttachmentVerificationTooltip from 'src/components/AttachmentVerificationTooltip/AttachmentVerificationTooltip'
import { useTranslate } from 'src/localization/Trns'
import {
  ReceiptVerificationStatus,
  receiptVerificationStatusToIcon,
  receiptVerificationStatusToTooltip,
} from 'src/models/receiptVerification.models'
import {
  TransactionDetailModel,
  TransactionDetailResponse,
} from 'src/models/transaction.models'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useRequestConfig } from 'src/queries/queries.utils'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface ReceiptVerificationCellProps {
  status?: ReceiptVerificationStatus
  reasons?: string[]
  transactionId?: string
}

export default function ReceiptVerificationCell({
  status,
  reasons,
  transactionId,
}: ReceiptVerificationCellProps): React.ReactElement | null {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()

  if (!status) return null

  const label = translate(receiptVerificationStatusToTooltip(status))

  const handleOpenAttachment = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    if (transactionId === undefined) return
    queryClient
      .fetchQuery({
        queryKey: transactionsQueryKeys.detail(transactionId),
        queryFn: () =>
          axios.get<TransactionDetailResponse>(
            `/transactions/${transactionId}`,
            requestConfig,
          ),
        retry: false,
      })
      .then((response) => {
        const detail = new TransactionDetailModel(response.data)
        const firstAttachment = detail.receiptAttachments[0]
        if (firstAttachment !== undefined) {
          dispatch(
            createSetIsAttachmentPopupOpen(true, undefined, firstAttachment),
          )
        }
      })
      .catch(() => undefined)
  }

  const icon = (
    <span
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 8px;
        border-radius: 8px;
        &:hover {
          background: ${base_colorSurfaceMutedHover};
        }
      `}
      aria-label={label}
    >
      {receiptVerificationStatusToIcon(status, 16)}
    </span>
  )

  const tooltipIcon = (
    <TooltipIcon
      placement="top"
      icon={icon}
      iconTestId={`receipt-verification-${status.toLowerCase()}`}
    >
      <AttachmentVerificationTooltip status={status} reasons={reasons} />
    </TooltipIcon>
  )

  return transactionId !== undefined ? (
    <button
      type="button"
      onClick={handleOpenAttachment}
      css={css`
        border: none;
        padding: 0;
        margin: 0;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </button>
  ) : (
    <div
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </div>
  )
}
